import React, { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { AITrainerSession, Game, Profile } from '../types';
import {
  Brain,
  MessageSquare,
  Calendar,
  Target,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/useTranslation';

const SESSIONS_PER_PAGE = 5;

export default function AITrainer() {
  const user = useStore((state) => state.user);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<AITrainerSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [sessionDetails, setSessionDetails] = useState<
    Record<string, { games: Game[]; profile: Profile | null }>
  >({});
  const [lastDoc, setLastDoc] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(
    new Set()
  );

  const fetchSessionDetails = useCallback(
    async (session: AITrainerSession) => {
      if (sessionDetails[session.id]) return; // Already loaded

      try {
        const games: Game[] = [];

        // Batch fetch games (limit to avoid too many requests)
        const gamePromises = session.gameIds
          .slice(0, 10)
          .map(async (gameId) => {
            try {
              const gameDoc = await getDoc(doc(db, 'games', gameId));
              if (gameDoc.exists()) {
                return {
                  id: gameDoc.id,
                  ...gameDoc.data(),
                  date: new Date(gameDoc.data().date),
                  createdAt: new Date(gameDoc.data().createdAt),
                } as Game;
              }
            } catch (err) {
              console.warn(`Could not fetch game ${gameId}:`, err);
            }
            return null;
          });

        const gameResults = await Promise.all(gamePromises);
        games.push(...(gameResults.filter((game) => game !== null) as Game[]));

        // Get user profile (cached after first fetch)
        let profile = null;
        const existingProfile = Object.values(sessionDetails)[0]?.profile;
        if (existingProfile) {
          profile = existingProfile;
        } else {
          try {
            const profileDoc = await getDoc(doc(db, 'profiles', user!.uid));
            if (profileDoc.exists()) {
              profile = {
                id: profileDoc.id,
                ...profileDoc.data(),
                createdAt: profileDoc.data().createdAt.toDate(),
              } as Profile;
            }
          } catch (err) {
            console.warn('Could not fetch profile:', err);
          }
        }

        setSessionDetails((prev) => ({
          ...prev,
          [session.id]: { games, profile },
        }));
      } catch (err) {
        console.error(`Error fetching details for session ${session.id}:`, err);
      }
    },
    [sessionDetails, user]
  );

  const fetchSessions = useCallback(
    async (isLoadMore = false) => {
      if (!user) return;

      try {
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        let q = query(
          collection(db, 'ai_trainer_sessions'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(SESSIONS_PER_PAGE)
        );

        if (isLoadMore && lastDoc) {
          q = query(
            collection(db, 'ai_trainer_sessions'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc'),
            startAfter(lastDoc),
            limit(SESSIONS_PER_PAGE)
          );
        }

        const querySnapshot = await getDocs(q);
        const sessionsData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt.toDate(),
          updatedAt: doc.data().updatedAt.toDate(),
        })) as AITrainerSession[];

        if (isLoadMore) {
          setSessions((prev) => [...prev, ...sessionsData]);
        } else {
          setSessions(sessionsData);
        }

        // Update pagination state
        const lastDocument = querySnapshot.docs[querySnapshot.docs.length - 1];
        setLastDoc(lastDocument || null);
        setHasMore(querySnapshot.docs.length === SESSIONS_PER_PAGE);

        // Pre-load details for first few sessions only
        const sessionsToPreload = sessionsData.slice(0, 2);
        for (const session of sessionsToPreload) {
          await fetchSessionDetails(session);
        }
      } catch (err) {
        console.error('Error fetching AI trainer sessions:', err);
        setError(t('messages.loadingError'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [user, lastDoc, fetchSessionDetails]
  );

  const loadMoreSessions = () => {
    if (!loadingMore && hasMore) {
      fetchSessions(true);
    }
  };

  const toggleSessionExpansion = async (sessionId: string) => {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) return;

    if (!expandedSessions.has(sessionId)) {
      // Load details when expanding
      await fetchSessionDetails(session);
      setExpandedSessions((prev) => new Set([...prev, sessionId]));
    } else {
      setExpandedSessions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [user]);

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4'></div>
          <p className='text-gray-600'>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-4xl mx-auto px-4 py-8'>
      <div className='flex items-center justify-between mb-8'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold text-primary-600 flex items-center gap-2 italic'>
            <Brain className='h-7 w-7 text-[#1b3968]' />
            {t('aiTrainer.title')}
          </h1>
          <p className='text-gray-600 mt-2'>
            {t('aiTrainer.subtitle')}
          </p>
        </div>
      </div>

      {error ? (
        <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'>
          {error}
        </div>
      ) : (
        <>
          {sessions.length === 0 ? (
            <div className='text-center py-12 bg-gray-50 rounded-lg'>
              <Brain className='h-16 w-16 text-gray-400 mx-auto mb-4' />
              <h3 className='text-lg font-medium text-gray-900 mb-2'>
                {t('aiTrainer.noSessions')}
              </h3>
              <p className='text-gray-600 mb-6'>
                {t('aiTrainer.createFirst')}
              </p>
              <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                <Link
                  to='/dashboard'
                  className='inline-flex items-center px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90'
                >
                  <Target className='h-5 w-5 mr-2' />
                  View Games
                </Link>
                <Link
                  to='/analytics'
                  className='inline-flex items-center px-4 py-2 border border-[#1b3968] text-[#1b3968] rounded-md hover:bg-[#1b3968]/5'
                >
                  <MessageSquare className='h-5 w-5 mr-2' />
                  View Analytics
                </Link>
              </div>
            </div>
          ) : (
            <div className='space-y-6'>
              {sessions.map((session) => {
                const details = sessionDetails[session.id];
                const games = details?.games || [];
                const isExpanded = expandedSessions.has(session.id);
                const isDetailsLoading = !details && isExpanded;

                return (
                  <div
                    key={session.id}
                    className='glass-card overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-[1.02]'
                  >
                    {/* Session Header - Always Visible */}
                    <div className='p-6'>
                      <div className='flex items-start justify-between'>
                        <div className='flex-1'>
                          <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                            {session.title}
                          </h3>

                          <div className='flex items-center gap-4 text-sm text-gray-600 mb-3'>
                            <div className='flex items-center gap-1'>
                              <Calendar className='h-4 w-4' />
                              <span>
                                {session.createdAt.toLocaleDateString()}
                              </span>
                            </div>
                            <div className='flex items-center gap-1'>
                              <Target className='h-4 w-4' />
                              <span>
                                {session.gameIds.length} game
                                {session.gameIds.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>

                          <p className='text-gray-700 text-sm line-clamp-2'>
                            {session.analysis.substring(0, 150)}...
                          </p>
                        </div>

                        <div className='ml-4 flex-shrink-0 flex items-center gap-2'>
                          <button
                            onClick={() => toggleSessionExpansion(session.id)}
                            className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                            title={isExpanded ? 'Show less' : 'Show more'}
                          >
                            <ChevronDown
                              className={`h-5 w-5 text-gray-400 transition-transform ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          <div
                            className='w-12 h-12 bg-[#1b3968]/10 rounded-full flex items-center justify-center cursor-pointer'
                            onClick={() =>
                              navigate(`/ai-trainer/${session.id}`)
                            }
                          >
                            <Brain className='h-6 w-6 text-[#1b3968]' />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Details */}
                    {isExpanded && (
                      <div className='border-t border-gray-200 bg-gray-50 p-6'>
                        {isDetailsLoading ? (
                          <div className='flex items-center justify-center py-8'>
                            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-[#1b3968]'></div>
                            <span className='ml-3 text-gray-600'>
                              {t('common.loading')}
                            </span>
                          </div>
                        ) : (
                          <>
                            {games.length > 0 && (
                              <div className='mb-4'>
                                <p className='text-sm font-medium text-gray-700 mb-2'>
                                  Games analyzed:
                                </p>
                                <div className='flex flex-wrap gap-2'>
                                  {games.map((game) => (
                                    <span
                                      key={game.id}
                                      className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800'
                                    >
                                      vs {game.opponent} (
                                      {game.date.toLocaleDateString()})
                                    </span>
                                  ))}
                                  {session.gameIds.length > games.length && (
                                    <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600'>
                                      +{session.gameIds.length - games.length}{' '}
                                      more games
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {session.drills.length > 0 && (
                              <div className='mb-4'>
                                <p className='text-sm font-medium text-gray-700 mb-2'>
                                  Recommended Drills ({session.drills.length}
                                  ):
                                </p>
                                <div className='flex flex-wrap gap-2'>
                                  {session.drills
                                    .slice(0, 6)
                                    .map((drill, index) => (
                                      <span
                                        key={index}
                                        className='inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800'
                                      >
                                        {drill.title}
                                      </span>
                                    ))}
                                  {session.drills.length > 6 && (
                                    <span className='inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600'>
                                      +{session.drills.length - 6} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            <div className='flex justify-end'>
                              <button
                                onClick={() =>
                                  navigate(`/ai-trainer/${session.id}`)
                                }
                                className='inline-flex items-center px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90 text-sm'
                              >
                                View Full Session
                                <MessageSquare className='h-4 w-4 ml-2' />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Load More Button */}
              {hasMore && (
                <div className='text-center py-6'>
                  <button
                    onClick={loadMoreSessions}
                    disabled={loadingMore}
                    className='inline-flex items-center px-6 py-3 border border-[#1b3968] text-[#1b3968] rounded-md hover:bg-[#1b3968]/5 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {loadingMore ? (
                      <>
                        <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-[#1b3968] mr-2'></div>
                        Loading more sessions...
                      </>
                    ) : (
                      <>
                        <Plus className='h-4 w-4 mr-2' />
                        {t('common.loadMoreSessions')}
                      </>
                    )}
                  </button>
                </div>
              )}

              {!hasMore && sessions.length > SESSIONS_PER_PAGE && (
                <div className='text-center py-4 text-gray-500 text-sm'>
                  You've reached the end of your AI training sessions
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
